import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { DownloadIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Flex,
	HStack,
	Icon,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	SimpleGrid,
	Td,
	Text,
	Tr,
	useColorModeValue,
	useDisclosure,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiDownload, FiFileText, FiImage, FiInfo } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "../PropertyModal/shared";
import { removeReportAsync, updateReportActiveAsync } from "../../redux/user/userSlice";
import { API } from "../../utils/http";
import { fullDateFormat } from "../../utils/types";
import { getDocumentBadge } from "../../utils/utils";
import Gallery from "../Gallery/Gallery";

function ReportsTableRow(props) {
	const { row, propertyId } = props;
	const dispatch = useDispatch();
	const textColor = useColorModeValue("gray.700", "white");
	const iconColor = useColorModeValue("blue.500", "blue.300");

	const deleteDocument = (reportId) => {
		console.log("delete: ", { reportId, propertyId });
		dispatch(removeReportAsync({ reportId, propertyId }));
	};
	const { t } = useTranslation();
	const handleGetDocument = () => {
		const payload = { propertyId, reportId: row.id, reportName: row.name };
		console.log(payload);
		API.put("get-report", payload).then((r) => {
			console.log(r);
			window.open(r.data.url, "_blank");
		});
	};
	const markAsActive = (documentId) => {
		const payload = { documentId, propertyId, type: row.type, active: true };
		console.log("mark document as active: ", payload);
		dispatch(updateReportActiveAsync(payload));
	};
	const markAsInactive = (documentId) => {
		const payload = { documentId, propertyId, type: row.type, active: false };
		console.log("mark document as active: ", payload);
		dispatch(updateReportActiveAsync(payload));
	};
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [images, setImages] = useState([]);
	const getReportPictures = () => {
		const payload = { propertyId, media: row.media, reportId: row.id };
		API.put("get-report-picture-urls", payload).then((r) => {
			console.log(r.data);
			setImages(r.data.media);
		});
	};

	const renderViewModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={onClose}
			size="4xl"
			icon={FiFileText}
			title={t("property.reportDetailsTitle")}
			subtitle={t("property.viewReportSubtitle")}
			footer={<ModalFooterActions onCancel={onClose} cancelLabel={t("misc.close")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection
						icon={FiInfo}
						title={t("property.reportSummary")}
						headerExtra={
							<Badge colorScheme={row.active ? "green" : "gray"} fontSize="xs" px={2} py={0.5} borderRadius="full">
								{row.active ? t("property.activeLabel") : t("property.inactiveLabel")}
							</Badge>
						}
					>
						<VStack spacing={3} align="stretch">
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiCalendar} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.addedDate")}:
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="medium">
									{moment(row.addedDate).format(fullDateFormat)}
								</Text>
							</HStack>
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiFileText} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.type")}:
									</Text>
								</HStack>
								<Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
									{t(`types.${row.type}`)}
								</Badge>
							</HStack>
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiFileText} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.number")}:
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="medium" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
									{row.number}
								</Text>
							</HStack>
						</VStack>
					</PropertyModalSection>

					{row.description && (
						<PropertyModalSection icon={FiInfo} title={t("property.descriptionLabel")}>
							<Text fontSize="sm" color="gray.700" bg="gray.50" p={3} borderRadius="md">
								{row.description}
							</Text>
						</PropertyModalSection>
					)}

					<PropertyModalSection icon={FiDownload} title={t("property.reportDocument")}>
						<Box
							p={3}
							border="1px"
							borderColor="gray.200"
							borderRadius="md"
							_hover={{ bg: "gray.50" }}
							cursor="pointer"
							onClick={() => handleGetDocument(row.id)}
						>
							<HStack justify="space-between">
								<HStack spacing={3}>
									<Icon as={FiFileText} color={iconColor} boxSize={4} />
									<VStack align="start" spacing={0}>
										<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
											{row.name}
										</Text>
										<Text fontSize="xs" color="gray.500">
											{t("property.clickToDownload")}
										</Text>
									</VStack>
								</HStack>
								<Icon as={DownloadIcon} color={iconColor} boxSize={4} />
							</HStack>
						</Box>
					</PropertyModalSection>
				</VStack>

				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection
						icon={FiImage}
						title={t("property.mediaGallery")}
						headerExtra={
							<Badge colorScheme="blue" fontSize="xs">
								{images.length}
							</Badge>
						}
					>
						{images.length > 0 ? (
							<Box>
								<Gallery items={images} />
							</Box>
						) : (
							<Box textAlign="center" py={6}>
								<Icon as={FiImage} color="gray.300" boxSize={8} mb={3} />
								<Text fontSize="sm" color="gray.500">
									{t("property.noMediaAttached")}
								</Text>
							</Box>
						)}
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Tr>
			<Td minWidth={{ sm: "250px" }} pl="0px">
				<Flex direction={"column"}>
					<Text fontSize="md" color={textColor}>
						{moment(row.addedDate).format(fullDateFormat)}
					</Text>
					{getDocumentBadge(row)}
				</Flex>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.name}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{t(`types.${row.type}`)}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.description}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.number}
				</Text>
			</Td>

			<Td>
				<Menu closeOnSelect={false}>
					<MenuButton>
						<Icon as={HamburgerIcon} />
					</MenuButton>
					<MenuList>
						<MenuItem
							onClick={() => {
								onOpen();
								getReportPictures();
							}}
						>
							View
						</MenuItem>
						{row.active ? (
							<MenuItem
								onClick={() => {
									markAsInactive(row.id);
								}}
							>
								Mark as inactive
							</MenuItem>
						) : (
							<MenuItem
								onClick={() => {
									markAsActive(row.id);
								}}
							>
								Mark as Active
							</MenuItem>
						)}
						<MenuItem
							onClick={() => {
								deleteDocument(row.id);
							}}
						>
							Delete
						</MenuItem>
					</MenuList>
				</Menu>
				{renderViewModal()}
			</Td>
		</Tr>
	);
}

export default ReportsTableRow;
